/**
 * Species-name language model for the carousel editor.
 *
 * A species can carry up to four names — English (100% coverage, the only
 * one guaranteed to exist), scientific/Latin (100% coverage), Russian
 * (~93% via eBird's `ru` locale), and Kazakh (~5%, sourced from a curated
 * Kazakhstan checklist — see app/api/admin/enrich-bird-names). The template
 * has three display slots (largest/medium/smallest — see SpeciesSlideData's
 * namePrimary/Secondary/Tertiary), and which of the four names lands in
 * which slot is a free choice, not a fixed pairing: one person's post wants
 * Kazakh headline → Russian → English, another's wants just English +
 * Latin. See NameOrder / assignNameSlots().
 */

export interface SpeciesNameSet {
  /** Always present — the one name guaranteed for every species. */
  nameEn: string;
  nameScientific: string | null;
  nameRu: string | null;
  nameKz: string | null;
}

export type NameField = "en" | "kz" | "ru" | "scientific";

export const NAME_FIELDS: { id: NameField; label: string }[] = [
  { id: "en", label: "English" },
  { id: "kz", label: "Kazakh" },
  { id: "ru", label: "Russian" },
  { id: "scientific", label: "Scientific" },
];

/** Which raw field fills the largest/medium/smallest slot — `null` leaves
 * that slot empty. Order matters; a field can only be used once. */
export type NameOrder = [NameField | null, NameField | null, NameField | null];

/** English headline + Latin underneath — the default for anyone whose
 * browser locale isn't Russian/Kazakh (see detectDefaultNameOrder). */
export const ENGLISH_PRESET: NameOrder = ["en", "scientific", null];
export const RUSSIAN_PRESET: NameOrder = ["ru", "scientific", "en"];
export const KAZAKH_PRESET: NameOrder = ["kz", "scientific", "en"];

export interface NameSlots {
  namePrimary: string | null;
  nameSecondary: string | null;
  nameTertiary: string;
}

function fieldValue(names: SpeciesNameSet, field: NameField | null): string | null {
  switch (field) {
    case "en": return names.nameEn;
    case "kz": return names.nameKz;
    case "ru": return names.nameRu;
    case "scientific": return names.nameScientific;
    default: return null;
  }
}

/**
 * Maps a name set onto the template's three generic display slots (primary
 * = largest/top, secondary = medium, tertiary = smallest, bottom-anchored)
 * per the user's chosen field order. A field missing for this particular
 * species (e.g. Kazakh chosen but this bird has none) simply renders that
 * slot empty for this card — speciesNameLayout() already collapses empty
 * slots instead of reserving blank space, so a per-species gap doesn't
 * leave a hole. If a fallback matters to the user (e.g. "show English when
 * Kazakh is missing"), that's what putting English in a later slot is for —
 * it's not implicit.
 */
export function assignNameSlots(names: SpeciesNameSet, order: NameOrder): NameSlots {
  return {
    namePrimary: fieldValue(names, order[0]),
    nameSecondary: fieldValue(names, order[1]),
    nameTertiary: fieldValue(names, order[2]) ?? "",
  };
}

const STORAGE_KEY = "liferradar_name_order";

/**
 * Picks the name order a *new* carousel should open with — a prior explicit
 * choice (localStorage) wins, otherwise the browser's *primary* language
 * decides (navigator.language, not the full navigator.languages list — an
 * OS with Russian installed as a third-choice fallback language shouldn't
 * flip a primarily-English visitor into Russian). No IP geolocation: it
 * would need a paid lookup service and collects a signal the browser
 * already exposes for free. Kazakh's ISO 639-1 code is "kk" (not "kz" —
 * that's the *country* code) — don't conflate them.
 */
export function detectDefaultNameOrder(): NameOrder {
  if (typeof window === "undefined") return ENGLISH_PRESET;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length === 3) return parsed as NameOrder;
    }
  } catch {
    // corrupt/inaccessible storage — fall through to locale sniffing.
  }

  const base = navigator.language?.toLowerCase().split("-")[0];
  if (base === "ru") return RUSSIAN_PRESET;
  if (base === "kk") return KAZAKH_PRESET;
  return ENGLISH_PRESET;
}

/** Remembers an explicit name-order choice so the next carousel opens with it. */
export function saveNameOrderPreference(order: NameOrder): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  } catch {
    // ignore — not worth surfacing a failure for a soft preference
  }
}

/** True once anything other than the plain-English preset is active — used
 * to decide whether the name-order picker should start expanded. */
export function isNonEnglishOrder(order: NameOrder): boolean {
  return order.some((f) => f === "ru" || f === "kz");
}
